import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container, Typography, Box, Paper, TextField, Button, MenuItem,
  Select, FormControl, InputLabel, FormHelperText, Alert, Chip, FormControlLabel, Checkbox,
  CircularProgress
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import QuillEditor from '../../components/Common/QuillEditor';
import forumService from '../../services/forumService';
import { useAuth } from '../../contexts/AuthContext';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['clean']
  ]
};

const DRAFT_KEY = 'wedigo:forum:newThreadDraft';
const MAX_TAGS = 5;
const MAX_TAG_LENGTH = 30;

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const clearDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* nincs tárhely */ }
};

const ForumCreate = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  // Piszkozat: a lap újratöltése / véletlen elnavigálás után sem vész el a beírt szöveg
  const initialDraft = useRef(loadDraft()).current;
  const hasDraft = !!(initialDraft && (initialDraft.title || stripHtml(initialDraft.content)));

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [title, setTitle] = useState(initialDraft?.title ?? '');
  const [content, setContent] = useState(initialDraft?.content ?? '');
  const [categoryId, setCategoryId] = useState(initialDraft?.categoryId ?? '');
  const [isAnonymous, setIsAnonymous] = useState(initialDraft?.isAnonymous ?? false);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(initialDraft?.tags ?? []);
  const [touched, setTouched] = useState({});
  const [draftNotice, setDraftNotice] = useState(hasDraft);
  const [savedAt, setSavedAt] = useState(null);
  const [editorKey, setEditorKey] = useState(0);

  const titleRef = useRef(null);
  const editorBoxRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { state: { from: '/forum/uj-tema' } });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    forumService.getCategories().then(res => {
      if (res.success) {
        setCategories(res.data.categories);
        const slug = searchParams.get('category');
        if (slug && !initialDraft?.categoryId) {
          const cat = res.data.categories.find(c => c.slug === slug);
          if (cat) setCategoryId(cat.id);
        }
      }
    }).catch(() => setError('Nem sikerült betölteni a kategóriákat.'));
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Automatikus mentés (kis késleltetéssel, hogy ne írjunk minden billentyűre)
  useEffect(() => {
    const empty = !title && !stripHtml(content) && tags.length === 0;
    const t = setTimeout(() => {
      try {
        if (empty) {
          localStorage.removeItem(DRAFT_KEY);
        } else {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, content, categoryId, isAnonymous, tags }));
          setSavedAt(new Date());
        }
      } catch { /* privát mód / tele a tárhely – nem kritikus */ }
    }, 600);
    return () => clearTimeout(t);
  }, [title, content, categoryId, isAnonymous, tags]);

  // Azonnali validáció
  const errors = useMemo(() => {
    const e = {};
    if (!categoryId) e.categoryId = 'Válassz kategóriát.';
    const t = title.trim();
    if (!t) e.title = 'Add meg a téma címét.';
    else if (t.length < 5) e.title = `A cím legalább 5 karakter legyen (még ${5 - t.length} kell).`;
    const plain = stripHtml(content);
    if (!plain) e.content = 'Írd le a kérdésedet vagy tapasztalataidat.';
    else if (plain.length < 10) e.content = `A tartalom legalább 10 karakter legyen (még ${10 - plain.length} kell).`;
    else if (content.length > 20000) e.content = 'A tartalom túl hosszú (max. 20 000 karakter formázással együtt).';
    return e;
  }, [categoryId, title, content]);

  const touch = (field) => setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  const showError = (field) => (touched[field] ? errors[field] : undefined);

  const addTag = (raw) => {
    const newTag = raw.trim().replace(/^#+/, '').toLowerCase().slice(0, MAX_TAG_LENGTH);
    if (!newTag) return;
    setTags(prev => (prev.includes(newTag) || prev.length >= MAX_TAGS ? prev : [...prev, newTag]));
    setTagInput('');
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length) {
      setTags(tags.slice(0, -1));
    }
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const discardDraft = () => {
    clearDraft();
    setTitle(''); setContent(''); setTags([]); setIsAnonymous(false); setTouched({});
    setEditorKey(k => k + 1); // a szerkesztő újraépítése, hogy kiürüljön
    setDraftNotice(false);
    setSavedAt(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ categoryId: true, title: true, content: true });
    if (Object.keys(errors).length > 0) {
      setError(null);
      if (errors.categoryId || errors.title) titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else editorBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // A még be nem küldött címke-szöveget se veszítsük el
    const finalTags = tagInput.trim() && tags.length < MAX_TAGS
      ? [...new Set([...tags, tagInput.trim().replace(/^#+/, '').toLowerCase().slice(0, MAX_TAG_LENGTH)])]
      : tags;

    setLoading(true);
    setError(null);
    try {
      const res = await forumService.createThread({ title: title.trim(), content, categoryId, isAnonymous, tags: finalTags });
      if (res.success) {
        clearDraft();
        navigate(`/forum/tema/${res.data.thread.id}`);
      } else {
        setError(res.message || 'Hiba a téma létrehozásakor.');
      }
    } catch (err) {
      setError(err?.message || 'Hiba a téma létrehozásakor. A beírt szöveg megmaradt, próbáld újra.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Új téma – Wedigo Fórum</title>
      </Helmet>

      <Container maxWidth="md" sx={{ py: { xs: 2.5, md: 4 } }}>
        <Button component={Link} to="/forum" startIcon={<ArrowBack />} sx={{ mb: 1.5, ml: -1, textTransform: 'none', minHeight: 40 }}>
          Vissza a fórumhoz
        </Button>

        <Typography variant="h4" component="h1" fontFamily="Playfair Display, serif" fontWeight={700} gutterBottom
          sx={{ fontSize: { xs: '1.6rem', md: '2.1rem' } }}>
          Új téma indítása
        </Typography>

        {draftNotice && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            onClose={() => setDraftNotice(false)}
            action={<Button color="inherit" size="small" onClick={discardDraft}>Elvetés</Button>}
          >
            Visszaállítottuk a korábban megkezdett piszkozatodat.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <FormControl fullWidth sx={{ mb: 2.5 }} required error={!!showError('categoryId')}>
              <InputLabel>Kategória</InputLabel>
              <Select
                value={categoryId}
                onChange={e => { setCategoryId(e.target.value); touch('categoryId'); }}
                onBlur={() => touch('categoryId')}
                label="Kategória"
              >
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id} sx={{ whiteSpace: 'normal', minHeight: 44 }}>
                    {cat.icon} {cat.name}
                  </MenuItem>
                ))}
              </Select>
              {showError('categoryId') && <FormHelperText>{errors.categoryId}</FormHelperText>}
            </FormControl>

            <TextField
              inputRef={titleRef}
              label="Téma címe"
              fullWidth
              required
              value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={() => touch('title')}
              sx={{ mb: 2.5 }}
              inputProps={{ maxLength: 200 }}
              error={!!showError('title')}
              helperText={
                <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span>{showError('title') || 'Foglald össze röviden, miről szól a kérdésed.'}</span>
                  <span>{title.length}/200</span>
                </Box>
              }
            />

            <Typography variant="subtitle2" color={showError('content') ? 'error' : 'text.secondary'} gutterBottom>
              Tartalom *
            </Typography>
            <Box
              ref={editorBoxRef}
              onBlur={() => touch('content')}
              sx={{
                mb: showError('content') ? 0.5 : 2.5,
                '& .ql-container': { minHeight: 180, fontSize: 15 },
                '& .ql-editor': { minHeight: 180, wordBreak: 'break-word', overflowWrap: 'anywhere' },
                '& .ql-toolbar': { display: 'flex', flexWrap: 'wrap' },
                border: `1px solid ${showError('content') ? theme.palette.error.main : theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <QuillEditor
                key={editorKey}
                value={content}
                onChange={setContent}
                modules={QUILL_MODULES}
                placeholder="Írd le a kérdésedet vagy tapasztalataidat..."
              />
            </Box>
            {showError('content') && (
              <FormHelperText error sx={{ mb: 2.5, mx: 1.75 }}>{errors.content}</FormHelperText>
            )}

            <TextField
              label={`Címkék (${tags.length}/${MAX_TAGS})`}
              fullWidth
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={() => addTag(tagInput)}
              disabled={tags.length >= MAX_TAGS}
              sx={{ mb: 1 }}
              inputProps={{ maxLength: MAX_TAG_LENGTH }}
              helperText={tags.length >= MAX_TAGS
                ? 'Elérted a maximum címkeszámot.'
                : 'Enter vagy vessző a hozzáadáshoz – pl. fotós, veszprém, riport stílus'}
            />
            {tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                {tags.map(tag => (
                  <Chip key={tag} label={`#${tag}`} size="small" onDelete={() => removeTag(tag)}
                    sx={{ maxWidth: '100%', bgcolor: alpha(theme.palette.secondary.main, 0.2) }} />
                ))}
              </Box>
            )}

            <FormControlLabel
              control={<Checkbox checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>Névtelen közzététel</Typography>
                  <Typography variant="caption" color="text.secondary">
                    A neved helyett "Névtelen felhasználó" jelenik meg (az adminok látják az azonosítód).
                  </Typography>
                </Box>
              }
              sx={{ mb: 2.5, alignItems: 'flex-start', mr: 0 }}
            />

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ minHeight: 48, px: 4, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
              >
                {loading ? 'Közzététel...' : 'Téma indítása'}
              </Button>
              <Button component={Link} to="/forum" size="large" sx={{ minHeight: 48, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                Mégse
              </Button>
              {savedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: { sm: 'auto' } }}>
                  <CheckCircleOutline sx={{ fontSize: 14 }} />
                  Piszkozat mentve {savedAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ForumCreate;
